import { useState, useMemo } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import type { ProjectDocType, TimeEntryDocType } from '@ast-data/shared';
import { projectLabelText } from '../projects/projectLabelText';
import TimeEntryConflictDialog from './TimeEntryConflictDialog';
import { type TimeEntryVariant } from './timeEntryVariant';

/**
 * Why a save was refused as a double booking — and the way to the entry that
 * refused it.
 *
 * Two things the bare sentence used to leave out, both of which are the point
 * when the conflict is cross-project: WHICH project the other booking is on,
 * and how to get to it.
 *
 * What it opens is a READ-ONLY view of that entry (TimeEntryConflictDialog),
 * not the editor. The conflict is regularly on a Maßnahme the person entering
 * the hours has nothing to do with — and the office editor is precisely what
 * such a user cannot open. Reading is not the problem; acting on it is, and the
 * dialog names the people who can.
 *
 * Nothing here navigates: at this moment there is unsaved input on screen, and
 * leaving the page would throw it away. The way on to the editor lives inside
 * the dialog, in a new tab.
 */
export interface TimeEntryOverlapNoticeProps {
    /** The entry already occupying those hours, as `saveTimeEntry` returned it. */
    overlap: TimeEntryDocType;
    variant: TimeEntryVariant;
}

export default function TimeEntryOverlapNotice({
    overlap,
    variant
}: TimeEntryOverlapNoticeProps) {
    // Read here rather than through a prop: all three callers (both grids and
    // the form dialog) would otherwise have to thread the project list through
    // for one label, and the whole database is local anyway.
    const projectsQuery = useStableQuery<ProjectDocType>({});
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useMemo(
        () => projectsDocs.map((doc) => doc.toMutableJSON() as ProjectDocType),
        [projectsDocs]
    );
    const project = (projects as ProjectDocType[]).find(
        (candidate) => candidate.id === overlap.projectId
    );

    const [detailOpen, setDetailOpen] = useState(false);

    return (
        <Box>
            <FormattedMessage
                id={variant.overlapErrorId}
                values={{
                    worker: overlap.workerName || '—',
                    from: overlap.startTime,
                    to: overlap.endTime,
                    // A project the user cannot see (or one since deleted) still
                    // has to say something — the conflict is real either way.
                    project: project ? projectLabelText(project) : '—'
                }}
            />{' '}
            <Link
                component="button"
                type="button"
                onClick={() => setDetailOpen(true)}
                sx={{ whiteSpace: 'nowrap', verticalAlign: 'baseline' }}
            >
                <FormattedMessage id="timeEntry.overlapShow" />
            </Link>
            {/* Mounted only while open: three of these notices can stand under
                one another (a crew booked in one go), and each would otherwise
                keep its own project query alive for a dialog nobody opened. */}
            {detailOpen && (
                <TimeEntryConflictDialog
                    overlap={overlap}
                    variant={variant}
                    open
                    onClose={() => setDetailOpen(false)}
                />
            )}
        </Box>
    );
}
